import { forwardRef } from 'react';

import { SixStarIcon } from '@wwe-portal/ui/components/icons';
import { StarOutlineIcon } from '@wwe-portal/ui/components/icons/star-outline-icon';
import { type RosterLevelType } from '@wwe-portal/entities/roster.type';
import { cn } from '@wwe-portal/ui/lib/lib';
import { safeBound } from '@wwe-portal/ui/lib/math';

import { HollowLevelStar, LevelStar } from './level-star';
import { ClickableDiv } from '../../../components/button';
import { Each } from '../../../components/utils';

export interface SelectableRosterStarProps {
  rate: number;
  maxRate: number;
  level: RosterLevelType;
  selected?: number;
  onClick?: (selectedStar: number) => void;
  size?: number;
  className?: string;
  hollowStar?: number;
  isStoreRevamp?: boolean;
  starClassName?: string;
}

export const SelectableRosterStar = forwardRef<
  HTMLDivElement,
  SelectableRosterStarProps
>(
  (
    {
      onClick,
      maxRate,
      selected,
      size = 21,
      rate,
      className,
      level,
      hollowStar = 0,
      isStoreRevamp,
      starClassName,
    },
    ref
  ) => {
    const safeRate = safeBound(rate, selected ?? 0, maxRate);
    const unRate = maxRate - safeRate;
    const isHollowStar = (index: number) =>
      index + 1 <= hollowStar &&
      (selected == null || selected < hollowStar + rate);

    if (rate > 5 && isStoreRevamp) {
      return (
        <div className={cn('flex items-center', className)}>
          <SixStarIcon variant={level} className={cn('', starClassName)} />
        </div>
      );
    }

    return (
      <div
        className={cn('flex cursor-pointer items-center gap-4', className)}
        key={unRate}
        ref={ref}
      >
        <Each
          data={Array(safeRate).fill(0)}
          render={(_, index) => (
            <ClickableDiv key={index} onClick={() => onClick?.(index + 1)}>
              <LevelStar
                className={cn('drop-shadow-md', starClassName, {
                  'animate-drop-shadow-pulse':
                    selected != null &&
                    index + 1 > rate &&
                    index + 1 <= selected,
                })}
                level={level}
                size={size}
              />
            </ClickableDiv>
          )}
        />
        {unRate > 0 && (
          <Each
            data={Array(unRate).fill(0)}
            render={(_, index) => {
              return (
                <ClickableDiv
                  key={index}
                  onClick={() => onClick?.(index + 1 + safeRate)}
                >
                  {isHollowStar(index) ? (
                    <HollowLevelStar
                      className={cn('drop-shadow-md')}
                      level={level}
                      size={size}
                    />
                  ) : (
                    <StarOutlineIcon
                      className="text-text300 opacity-35 drop-shadow-md"
                      height={size}
                      width={size}
                    />
                  )}
                </ClickableDiv>
              );
            }}
          />
        )}
      </div>
    );
  }
);

SelectableRosterStar.displayName = 'Rating';
